import React, { useEffect, useState } from 'react';
import type { StaysProperty } from '../../../../api/stays-properties-client';
import { addRooms, createRoomType } from '../../../../api/stays-hotel-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { FormModal } from '../../../modal/form-modal';
import { Stepper } from '../../../stepper/stepper';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { toast } from '../../../../utils/toast';
import { HOTEL_MAX, ROOM_TYPE_NAME_MAX, splitRoomList } from './properties-model';
import { Inline, PasteArea } from './properties.styled';

export type HotelRoomsMode = { property: StaysProperty; kind: 'type' | 'rooms' } | null;

/**
 * A HOTEL'S ROOMS (Independent Stays IS9.6) from Your properties: "Add a room
 * type" (a name, how many it sleeps, its rooms) and "Add rooms" to a type.
 * Each room gets its own listing with the type's times and guests
 * (BE src/stays/room-types.js). Removing or moving rooms stays on the
 * listing's own page (retire), where the stays on it are checked first.
 */
export const HotelRoomsModal: React.FC<{ mode: HotelRoomsMode; onClose: () => void; onSaved: () => void }> = ({
  mode,
  onClose,
  onSaved,
}) => {
  const property = mode ? mode.property : null;
  const types = (property && property.room_types) || [];
  const [name, setName] = useState('');
  const [guests, setGuests] = useState(2);
  const [typeId, setTypeId] = useState('');
  const [text, setText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName('');
    setGuests(2);
    setTypeId(types[0] ? types[0].id : '');
    setText('');
    setErrors({});
    setProblem(null);
  }, [mode]);
  const list = splitRoomList(text);
  const addingType = !!mode && mode.kind === 'type';

  const save = async () => {
    if (!property) return;
    const local: Record<string, string> = {};
    if (addingType && !name.trim()) local.name = 'Name this room type, for example Twin.';
    if (!addingType && !typeId) local.room_type = 'Choose the room type.';
    if (!addingType && !list.length) local.rooms = 'Add at least one room number.';
    if (Object.keys(local).length) {
      setErrors(local);

      return;
    }
    setSaving(true);
    setErrors({});
    setProblem(null);
    try {
      const rooms = list.map(label => ({ label }));
      const out = addingType
        ? await createRoomType(property.building_id, { name: name.trim(), max_guests: guests, ...(rooms.length ? { rooms } : {}) })
        : await addRooms(typeId, rooms);
      toast.success(
        addingType
          ? `${out.name} added to ${property.name}.`
          : `${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'} added to ${out.name}.`
      );
      onSaved();
    } catch (e) {
      const se = e instanceof StaysApiError ? e : null;
      if (se && se.code === 'INVALID_FIELDS') {
        const f = se.fields || {};
        const mapped: Record<string, string> = {};
        for (const [k, v] of Object.entries(f)) mapped[k.startsWith('rooms') ? 'rooms' : k] = mapped[k.startsWith('rooms') ? 'rooms' : k] || v;
        setErrors(mapped);
      } else setProblem(refusalText(se, 'Could not add the rooms. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const title = !property ? 'Rooms' : addingType ? `Add a room type to ${property.name}` : `Add rooms to ${property.name}`;
  const cta = addingType
    ? 'Add room type'
    : list.length
      ? `Add ${list.length} ${list.length === 1 ? 'room' : 'rooms'}`
      : 'Add rooms';

  return (
    <FormModal
      open={!!mode}
      title={title}
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={save} loading={saving} loadingLabel="Adding…">
            {cta}
          </AppButton>
        </Inline>
      }
    >
      {addingType ? (
        <FieldRow>
          <Field>
            <Label htmlFor="hotel-type-name">Room type</Label>
            <Input
              id="hotel-type-name"
              value={name}
              maxLength={ROOM_TYPE_NAME_MAX}
              placeholder="For example Twin"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'hotel-type-name-error' : undefined}
              onChange={e => setName(e.target.value)}
            />
            <FieldError id="hotel-type-name-error" message={errors.name} />
          </Field>
          <Field>
            <Label as="span">Sleeps</Label>
            <Stepper value={guests} min={1} max={30} label="guests" onChange={setGuests} />
          </Field>
        </FieldRow>
      ) : (
        <Field>
          <Label htmlFor="hotel-rooms-type">Room type</Label>
          <Select
            id="hotel-rooms-type"
            value={typeId}
            aria-invalid={!!errors.room_type}
            onChange={e => setTypeId(e.target.value)}
          >
            {types.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.rooms} {t.rooms === 1 ? 'room' : 'rooms'})
              </option>
            ))}
          </Select>
          <FieldError id="hotel-rooms-type-error" message={errors.room_type} />
        </Field>
      )}
      <Field>
        <Label htmlFor="hotel-rooms-list">{addingType ? 'Its room numbers (you can add them later)' : 'Room numbers'}</Label>
        <PasteArea
          id="hotel-rooms-list"
          value={text}
          placeholder="For example 301-306, 310"
          aria-invalid={!!errors.rooms}
          aria-describedby={errors.rooms ? 'hotel-rooms-list-error' : 'hotel-rooms-list-hint'}
          onChange={e => setText(e.target.value)}
        />
        <FieldHint id="hotel-rooms-list-hint">
          One per line, separated by commas, or a range such as 301-306. A hotel holds up to {HOTEL_MAX} rooms. Each room
          gets its own listing with its room type&apos;s times.
        </FieldHint>
        <FieldError id="hotel-rooms-list-error" message={errors.rooms} />
      </Field>
      {problem ? <FieldError id="hotel-rooms-problem" message={problem} /> : null}
    </FormModal>
  );
};
